import React, { useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { useAtom } from 'jotai';

import { PageLoading } from '@/components/0-reusable-components/page-loading';
import User from '@/models/user/user.model';
import { authorizedChimeraAtom, meAtom } from '@/store/me.store';
import { authorizedDexAtom, dexUserAtom } from '@/store/user/dex-user.store';
import Analytics from '@/utils/common/analytics';
import { redirectToSignIn, redirectToUnauthorized } from '@/utils/services/accounts';
import { getUserPictureUrl } from '@/utils/services/chimera';
import SentryClient from '@/utils/services/sentry';

type Props = {
  children: React.ReactElement;
};

const onAuthenticateFail = (): void => {
  const currentUrl = window.location.href;
  redirectToSignIn(currentUrl);
  return; // no-op
};

const onAuthorizeFail = (): void => {
  redirectToUnauthorized();
  return; // no-op
};

const Authenticated = ({ children }: Props): React.ReactElement | null => {
  const [storedMe, setMe] = useAtom(meAtom);
  const [, setDexUser] = useAtom(dexUserAtom);
  const [authorizedChimera] = useAtom(authorizedChimeraAtom);
  const [authorizedDex] = useAtom(authorizedDexAtom);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function fetchMeUser(): Promise<void> {
      try {
        const dexToken = localStorage.getItem('dexToken');

        if (!storedMe || !dexToken) {
          const [user, dexUser] = await Promise.all([
            await User.authenticate(onAuthenticateFail),
            await User.dexAuthenticate(),
          ]);

          User.authorizeChimera(user, onAuthorizeFail);
          User.authorizeDex(dexUser, onAuthorizeFail);

          setMe({ ...user, picture: getUserPictureUrl(user) });
          setDexUser({ ...dexUser });

          localStorage.setItem('dexToken', dexUser.token);

          Analytics.identifyMe(user);
        } else {
          Analytics.identifyMe(storedMe);
        }

        setIsLoading(false);
      } catch (error) {
        const reportError = error instanceof Error ? error : new Error('Could not authenticate');
        setIsLoading(false);
        SentryClient.captureError(reportError);
        redirectToUnauthorized();
      }
    }
    fetchMeUser();
  }, [authorizedChimera, authorizedDex]);

  return isLoading ? (
    <Flex width={'100vw'} height={'100vh'}>
      <PageLoading />
    </Flex>
  ) : (
    children
  );
};

export default Authenticated;
